import type { FilterRuleModel, PricingCampaignModel } from '@/shared/generated'

import {
	CircleDashedIcon,
	CircleFadingPlusIcon,
	CircleXIcon,
	Share2Icon,
	ShuffleIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
	EngineBadge,
	getPricingCampaignEngine,
	GlobalPcBadge,
	isGlobalPricingCampaign,
	usePricingCampaignsQuery,
} from '@/entities/pricing-campaign'

import { Button } from '@/shared/components/ui/button'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from '@/shared/components/ui/command'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { ampli } from '@/shared/lib/ampli'
import { useModals } from '@/shared/lib/modal-manager'
import { truncate } from '@/shared/tools/utils'

const firstCharSort = (a: PricingCampaignModel, b: PricingCampaignModel) => {
	const nameA = (a?.name || '').toLowerCase().charAt(0),
		nameB = (b?.name || '').toLowerCase().charAt(0)
	if (nameA < nameB) return -1
	if (nameA > nameB) return 1

	return 0
}

type ProductsPricingCampaignsActionsGroupProps = {
	isAssignToAnchorManagementDisabled: boolean
	isPricingCampaignsActionsDisabled?: boolean
	/**
	 * Handle when select campaign or trye to unassign
	 * @param campaign PricingCampaignModel | string
	 */
	onSelectCampaign(campaign: PricingCampaignModel | string): void

	/**
	 * Handle when create new campaign form submit
	 * @param temp - temporary campaign data
	 */
	onCreateCampaign(temp: {
		optimizationGroupId?: string | null
		scenarioId?: string
		draft?: {
			name?: string
			engine?: string
			assignment_filters?: FilterRuleModel[]
		}
	}): void
	onAssignToAnchorManagement(): void
	/**
	 * Scope for query
	 */
	scope: {
		only_global?: boolean
		globalPcSetId?: string
		optimization_group_id?: string
	}
}

export default function ProductsAssignActionsDropdownMenu(
	props: ProductsPricingCampaignsActionsGroupProps
) {
	const { t } = useTranslation()

	return (
		<DropdownMenu>
			<DropdownMenuTrigger
				asChild
				disabled={
					props.isPricingCampaignsActionsDisabled &&
					props.isAssignToAnchorManagementDisabled
				}
				data-testid='products-assign-actions-trigger'
			>
				<Button size='sm'>
					<ShuffleIcon />
					{t('app.assign', { defaultValue: 'Assign' })}
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent>
				<ProductsPricingCampaignsActionsGroup {...props} />
			</DropdownMenuContent>
		</DropdownMenu>
	)
}
const AssignProductsToPricingCampaignMenuItem = ({
	scope,
	onSelectCampaign,
	disabled,
}: {
	scope: {
		only_global?: boolean
		optimization_group_id?: string
	}
	onSelectCampaign: (campaign: PricingCampaignModel) => void
	disabled?: boolean
}) => {
	const { t } = useTranslation()

	const { data: campaigns } = usePricingCampaignsQuery(scope, {
		placeholderData: [],
		select: (campaigns) =>
			campaigns
				.filter((campaign) =>
					!!campaign.id && !scope?.only_global
						? !!campaign.optimization_group_id
						: true
				)
				.sort(firstCharSort),
	})

	return (
		<DropdownMenuSub>
			<DropdownMenuSubTrigger
				data-testid='assign-to-existing-pc-trigger'
				disabled={disabled}
			>
				<CircleFadingPlusIcon className='size-4' />
				{t('assign_to.existing_pc.label', {
					defaultValue: 'Assign to existing PC',
				})}
			</DropdownMenuSubTrigger>
			<DropdownMenuSubContent className='p-0'>
				<Command className='w-80'>
					<CommandInput
						placeholder={t('search.pc.placeholder', {
							defaultValue: 'Search pricing campaigns...',
						})}
						autoFocus
					/>
					<CommandList>
						<CommandEmpty>
							{t('no_entity_found', {
								entity: t('entity.pricing_campaign'),
								defaultValue: 'No pricing campaigns found.',
							})}
						</CommandEmpty>
						<CommandGroup>
							{campaigns?.map((campaign) => (
								<CommandItem
									value={campaign.id}
									key={campaign.id}
									onSelect={() => onSelectCampaign(campaign)}
								>
									{truncate(campaign.name, 40)}
									<div className='ml-auto inline-flex items-center gap-1'>
										<EngineBadge
											className='ml-2'
											engine={getPricingCampaignEngine(campaign)}
										>
											{getPricingCampaignEngine(campaign)}
										</EngineBadge>
										{isGlobalPricingCampaign(campaign) && <GlobalPcBadge />}
									</div>
								</CommandItem>
							))}
						</CommandGroup>
					</CommandList>
				</Command>
			</DropdownMenuSubContent>
		</DropdownMenuSub>
	)
}

export function ProductsPricingCampaignsActionsGroup({
	scope,
	onCreateCampaign,
	onSelectCampaign,
	onAssignToAnchorManagement,
	isPricingCampaignsActionsDisabled,
	isAssignToAnchorManagementDisabled,
}: ProductsPricingCampaignsActionsGroupProps) {
	const { t } = useTranslation()

	const { showModal } = useModals()
	const handleCreatePricingCampagnAndAssignListTo = () => {
		ampli.settingsPricingCampaignsCreatePcClick({
			location: 'products-table',
		})
		showModal('CREATE_PRICING_CAMPAIGN_MODAL', {
			optimizationGroupId: scope.optimization_group_id,
			disableOptimizationGroups: true,
			onCreateCampaign,
		})
	}

	return (
		<DropdownMenuGroup>
			<DropdownMenuLabel>
				{t('assign_to.menu_title', { defaultValue: 'Assign to...' })}
			</DropdownMenuLabel>
			<AssignProductsToPricingCampaignMenuItem
				scope={scope}
				onSelectCampaign={onSelectCampaign}
				disabled={isPricingCampaignsActionsDisabled}
			/>
			<DropdownMenuItem
				data-testid='assign-to-new-pc-trigger'
				disabled={isPricingCampaignsActionsDisabled}
				onClick={handleCreatePricingCampagnAndAssignListTo}
			>
				<CircleDashedIcon className='size-4' />
				{t('assign_to.new_pc.label', { defaultValue: 'Assign to new PC' })}
			</DropdownMenuItem>
			<DropdownMenuItem
				data-testid='unassign-from-pc-trigger'
				disabled={isPricingCampaignsActionsDisabled}
				onClick={() => onSelectCampaign('')}
			>
				<CircleXIcon />
				{t('unassign_from.pc.label', { defaultValue: 'Unassign from PCs' })}
			</DropdownMenuItem>
			<DropdownMenuItem
				data-testid='assign-to-new-anchor-management-trigger'
				disabled={isAssignToAnchorManagementDisabled}
				onClick={onAssignToAnchorManagement}
			>
				<Share2Icon />
				{t('assign_to.anchor_management.label', {
					defaultValue: 'Assign to anchor management',
				})}
			</DropdownMenuItem>
		</DropdownMenuGroup>
	)
}
